import { CandleHistoryBuffer } from '../../../shared-web/candle-history';
import { Candle, MarketTick } from "@starx/shared-types";
import {
  calculateSMA,
  calculateEMA,
  calculateWMA,
  calculateHullMA,
  calculateSupertrend,
  calculateIchimoku,
  calculateParabolicSAR,
  calculateAlligator,
  calculateZigZag,
  calculateRSI,
  calculateStochastic,
  calculateMACD,
  calculateCCI,
  calculateWilliamsR,
  calculateAwesomeOscillator,
  calculateMomentum,
  calculateROC,
  calculateBollingerBands,
  calculateATR,
  calculateDonchianChannels,
  calculateKeltnerChannels,
  calculateADX,
  calculateFractals,
  calculateEnvelopes,
  calculateAroon,
  calculateVortex,
  calculateElderRay,
  calculateAcceleratorOscillator,
  calculateDeMarker,
} from "./indicators";

export type ChartType = "CANDLESTICK" | "LINE" | "AREA" | "BARS";
export type DrawingTool =
  | "CURSOR"
  | "TREND_LINE"
  | "RAY"
  | "HORIZONTAL_LINE"
  | "VERTICAL_LINE"
  | "RECTANGLE"
  | "ELLIPSE"
  | "TRIANGLE"
  | "ARROW"
  | "CHANNEL"
  | "PARALLEL_CHANNEL"
  | "FIBONACCI"
  | "FIBONACCI_FAN";
export type IndicatorId =
  | "SMA"
  | "EMA"
  | "WMA"
  | "HMA"
  | "SUPERTREND"
  | "ICHIMOKU"
  | "PSAR"
  | "ALLIGATOR"
  | "ZIGZAG"
  | "RSI"
  | "STOCHASTIC"
  | "MACD"
  | "CCI"
  | "WILLIAMS_R"
  | "AWESOME"
  | "MOMENTUM"
  | "ROC"
  | "BOLLINGER"
  | "ATR"
  | "DONCHIAN"
  | "KELTNER"
  | "ADX"
  | "FRACTALS"
  | "ENVELOPES"
  | "AROON"
  | "VORTEX"
  | "BULLS_BEARS"
  | "AC"
  | "DEMARKER";

export interface ChartDrawing {
  id: string;
  tool: Exclude<DrawingTool, "CURSOR">;
  color: string;
  start: { x: number; y: number };
  end: { x: number; y: number };
}

export class CanvasChart {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private candles: Candle[] = [];
  private chartType: ChartType = "CANDLESTICK";
  private latestPrice: number = 0;
  private isDegraded: boolean = false;
  private crosshair: { x: number; y: number } | null = null;
  private animationFrameId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private width: number = 800;
  private height: number = 400;
  private tradeOverlays: Array<{
    strikePrice: number;
    direction: "CALL" | "PUT";
    stake: string;
    payoutPercentage: string;
    expiryTime: number;
    entryTime?: number;
    kind?: "ACTIVE" | "PENDING";
    label?: string;
  }> = [];
  private activeIndicators = new Set<IndicatorId>();
  private frozenForSnapshot: boolean = false;
  private backupCandlesForSnapshot: Candle[] | null = null;
  private backupPriceForSnapshot: number = 0;
  private backupRenderedPriceForSnapshot: number = 0;
  private timeframeSeconds: number = 60;
  private tradeWindowDurationSeconds: number = 60;
  private renderedPrice: number = 0;
  private candleHistory = new CandleHistoryBuffer();
  private latestTickTimestampMs: number = 0;
  private priceAnimationFrom: number = 0;
  private priceAnimationStartedAt: number = 0;
  private readonly priceAnimationDurationMs: number = 900;
  private rolloverAnimationStartedAt: number = 0;
  private readonly rolloverAnimationDurationMs: number = 320;
  private renderedScaleMin: number = Number.NaN;
  private renderedScaleMax: number = Number.NaN;
  private scaleAnimationUpdatedAt: number = 0;
  private immediateScale: boolean = true;
  private autoScroll: boolean = true;
  private readonly liveAnchorRatio: number = 0.50;
  private zoomScale: number = 1.3;
  private panOffsetX: number = 0;
  private priceScaleZoom: number = 1;
  private priceScaleOffset: number = 0;
  private priceDecimals: number = 5;
  private instrumentSymbol: string = "";
  private navigationPointerId: number | null = null;
  private navigationLastX: number = 0;
  private navigationLastY: number = 0;
  private priceAxisDragging: boolean = false;
  private panVelocity: number = 0;
  private navigationLastAt: number = 0;
  private inertiaFrameId: number | null = null;
  private settlementPulse: { status: string; until: number } | null = null;
  private volumeVisible: boolean = true;
  private drawingTool: DrawingTool = "CURSOR";
  private drawingColor: string = "#f8fafc";
  private drawingStart: { x: number; y: number } | null = null;
  private drawingPreview: { x: number; y: number } | null = null;
  private activePointerId: number | null = null;
  private drawings: ChartDrawing[] = [];
  private selectedDrawingId: string | null = null;
  private drawingsChangedListeners = new Set<(drawings: ChartDrawing[]) => void>();
  private shortOrderLabel: boolean = false;
  private backupTradeOverlaysForSnapshot: Array<{
    strikePrice: number;
    direction: "CALL" | "PUT";
    stake: string;
    payoutPercentage: string;
    expiryTime: number;
    entryTime?: number;
    kind?: "ACTIVE" | "PENDING";
    label?: string;
  }> | null = null;

  public setShortOrderLabel(enabled: boolean) {
    this.shortOrderLabel = enabled;
  }

  public showSettlementResult(status: string) {
    this.settlementPulse = { status, until: Date.now() + 2200 };
  }

  public setTradeWindowDuration(seconds: number) {
    if (Number.isFinite(seconds) && seconds > 0) {
      this.tradeWindowDurationSeconds = seconds;
    }
  }

  private onGapDetectedCallback?: () => void;

  public onGapDetected(cb: () => void) {
    this.onGapDetectedCallback = cb;
  }

  public setFrozenForSnapshot(frozen: boolean) {
    this.frozenForSnapshot = frozen;
    if (frozen) {
      this.backupCandlesForSnapshot = [...this.candles];
      this.backupPriceForSnapshot = this.latestPrice;
      this.backupRenderedPriceForSnapshot = this.renderedPrice;
      this.backupTradeOverlaysForSnapshot = [...this.tradeOverlays];
      this.tradeOverlays = [];
      const baseTime = 1700000000;
      let previousClose = 1.0842;
      this.candles = Array.from({ length: 64 }, (_unused, index) => {
        const drift = Math.sin(index * 0.47) * 0.00018 + Math.cos(index * 0.19) * 0.00008;
        const close = previousClose + drift;
        const candle = {
          time: baseTime + index * 15,
          open: previousClose,
          high: Math.max(previousClose, close) + 0.00008 + (index % 3) * 0.000015,
          low: Math.min(previousClose, close) - 0.00007 - (index % 2) * 0.000015,
          close,
          volume: 8 + (index % 11),
        };
        previousClose = close;
        return candle;
      });
      this.latestPrice = previousClose;
      this.renderedPrice = previousClose;
    } else if (this.backupCandlesForSnapshot) {
      this.candles = this.backupCandlesForSnapshot;
      this.latestPrice = this.backupPriceForSnapshot;
      this.renderedPrice = this.backupRenderedPriceForSnapshot;
      if (this.backupTradeOverlaysForSnapshot) {
        this.tradeOverlays = this.backupTradeOverlaysForSnapshot;
        this.backupTradeOverlaysForSnapshot = null;
      }
      this.backupCandlesForSnapshot = null;
    }
  }

  public getFrozenForSnapshot(): boolean {
    return this.frozenForSnapshot;
  }

  public toggleSMA(enabled?: boolean) {
    this.setIndicatorEnabled(
      "SMA",
      enabled !== undefined ? enabled : !this.activeIndicators.has("SMA"),
    );
  }

  public getSMAEnabled(): boolean {
    return this.activeIndicators.has("SMA");
  }

  public setIndicatorEnabled(id: IndicatorId, enabled: boolean) {
    if (enabled) this.activeIndicators.add(id);
    else this.activeIndicators.delete(id);
    this.draw();
  }

  public getIndicatorEnabled(id: IndicatorId): boolean {
    return this.activeIndicators.has(id);
  }

  public getActiveIndicators(): IndicatorId[] {
    return [...this.activeIndicators];
  }

  public getCandles(): Candle[] {
    return this.candles;
  }

  public getCandlesCount(): number {
    return this.candles.length;
  }

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d", { alpha: false })!;
    this.setupDPI();
    this.setupEventListeners();
    this.startRenderLoop();
  }

  public remeasure() {
    const rect = this.canvas.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return;
    const dpr = window.devicePixelRatio || 1;
    this.width = rect.width; this.height = rect.height;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private setupDPI() {
    this.remeasure();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.remeasure());
      this.resizeObserver.observe(this.canvas);
    }
  }

  private recovering = false;
  private lastDrawWall = 0;
  private isAwaitingHistory = false;
  private chartTimezone: string = "UTC";
  private chartTimezoneLabel: string = "UTC";

  public setTimezone(tz: string, label: string = "UTC") {
    this.chartTimezone = tz || "UTC";
    this.chartTimezoneLabel = label || "UTC";
    this.draw();
  }

  public getTimezone(): string {
    return this.chartTimezone;
  }

  public getTimezoneLabel(): string {
    return this.chartTimezoneLabel;
  }

  public onResumeDetected: (() => void) | null = null;
  public pauseForRecovery() {
    if (this.candles.length === 0) {
      this.recovering = true;
    }
    if (this.inertiaFrameId !== null) cancelAnimationFrame(this.inertiaFrameId);
    this.inertiaFrameId = null; this.panVelocity = 0;
    this.priceAnimationStartedAt = this.rolloverAnimationStartedAt = 0;
  }

  public completeRecovery() {
    this.lastDrawWall = Date.now();
    this.remeasure();
    this.renderedPrice = this.priceAnimationFrom = this.latestPrice;
    this.priceAnimationStartedAt = this.rolloverAnimationStartedAt = 0;
    this.scaleAnimationUpdatedAt = 0;
    this.renderedScaleMin = this.renderedScaleMax = Number.NaN;
    this.immediateScale = true;
    this.recovering = false;
  }

  private setupEventListeners() {
    const pointFromClient = (clientX: number, clientY: number) => {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width))),
        y: Math.max(0, Math.min(1, (clientY - rect.top) / Math.max(1, rect.height))),
      };
    };
    const updatePointer = (clientX: number, clientY: number) => {
      const rect = this.canvas.getBoundingClientRect();
      this.crosshair = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
      if (this.drawingStart) this.drawingPreview = pointFromClient(clientX, clientY);
    };
    const beginDrawing = (clientX: number, clientY: number, pointerId: number) => {
      const point = pointFromClient(clientX, clientY);
      if (this.drawingTool === "CURSOR") {
        const selected = this.findDrawingAt(point);
        this.selectedDrawingId = selected?.id || null;
        this.draw();
        return;
      }
      if (this.activePointerId !== null) return;
      this.activePointerId = pointerId;
      this.drawingStart = point;
      this.drawingPreview = this.drawingStart;
    };
    const finishDrawing = (clientX: number, clientY: number, pointerId: number) => {
      if (!this.drawingStart || this.drawingTool === "CURSOR") return;
      if (this.activePointerId !== null && this.activePointerId !== pointerId && pointerId !== -1) return;
      const end = pointFromClient(clientX, clientY);
      const drawing: ChartDrawing = {
        id: globalThis.crypto?.randomUUID?.() || `DRAW-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        tool: this.drawingTool,
        color: this.drawingColor,
        start: this.drawingStart,
        end,
      };
      this.drawings.push(drawing);
      this.selectedDrawingId = drawing.id;
      this.drawingStart = null;
      this.drawingPreview = null;
      this.activePointerId = null;
      this.emitDrawingsChanged();
      this.draw();
    };

    // Pointer events cover mouse, pen, and touch. The terminal shell applies
    // touch-action:none so a drawing drag is not intercepted by page scroll.
    this.canvas.addEventListener("pointermove", (event) => {
      updatePointer(event.clientX, event.clientY);
      if (
        this.drawingTool === "CURSOR" &&
        this.navigationPointerId === event.pointerId
      ) {
        const deltaX = event.clientX - this.navigationLastX;
        const deltaY = event.clientY - this.navigationLastY;
        this.navigationLastX = event.clientX;
        this.navigationLastY = event.clientY;
        if (this.priceAxisDragging && Math.abs(deltaY) >= 0.5) {
          this.applyPriceScale(deltaY < 0 ? 1.018 : 1 / 1.018);
          event.preventDefault();
        } else if (Math.abs(deltaX) >= 0.5) {
          const now = performance.now();
          const elapsed = Math.max(1, now - this.navigationLastAt);
          this.panVelocity = deltaX / elapsed;
          this.navigationLastAt = now;
          this.applyPan(deltaX);
          event.preventDefault();
        }
      }
      if (this.drawingStart) event.preventDefault();
    });
    this.canvas.addEventListener("pointerdown", (event) => {
      beginDrawing(event.clientX, event.clientY, event.pointerId);
      if (this.drawingTool === "CURSOR") {
        this.navigationPointerId = event.pointerId;
        this.navigationLastX = event.clientX;
        this.navigationLastY = event.clientY;
        this.navigationLastAt = performance.now();
        this.panVelocity = 0;
        if (this.inertiaFrameId !== null) cancelAnimationFrame(this.inertiaFrameId);
        this.inertiaFrameId = null;
        const rect = this.canvas.getBoundingClientRect();
        this.priceAxisDragging = event.clientX - rect.left >= Math.max(0, rect.width - 65);
        this.canvas.setPointerCapture?.(event.pointerId);
      }
      if (this.drawingStart) {
        this.canvas.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      }
    });
    this.canvas.addEventListener("pointerup", (event) => {
      finishDrawing(event.clientX, event.clientY, event.pointerId);
      if (this.navigationPointerId === event.pointerId) {
        this.navigationPointerId = null;
        if (!this.priceAxisDragging) this.startPanInertia();
      }
      this.priceAxisDragging = false;
      this.canvas.releasePointerCapture?.(event.pointerId);
    });
    this.canvas.addEventListener("pointercancel", (event) => {
      this.activePointerId = null;
      this.drawingStart = null;
      this.drawingPreview = null;
      if (this.navigationPointerId === event.pointerId)
        this.navigationPointerId = null;
      this.priceAxisDragging = false;
      this.canvas.releasePointerCapture?.(event.pointerId);
    });

    this.canvas.addEventListener(
      "wheel",
      (event) => {
        if (this.drawingTool !== "CURSOR") return;
        event.preventDefault();
        const rect = this.canvas.getBoundingClientRect();
        const cursorX = event.clientX - rect.left;
        if (cursorX >= Math.max(0, rect.width - 65)) {
          this.applyPriceScale(event.deltaY < 0 ? 1.12 : 1 / 1.12);
        } else {
          this.applyZoom(event.deltaY < 0 ? 1.12 : 1 / 1.12, cursorX);
        }
      },
      { passive: false },
    );
    this.canvas.addEventListener("dblclick", (event) => {
      if (this.drawingTool !== "CURSOR") return;
      event.preventDefault();
      this.resetView();
    });

    // Mouse fallback keeps older embedded webviews functional without
    // double-starting when browsers emit both pointer and mouse events.
    this.canvas.addEventListener("mousemove", (event) => updatePointer(event.clientX, event.clientY));
    this.canvas.addEventListener("mousedown", (event) => {
      beginDrawing(event.clientX, event.clientY, -1);
      if (this.drawingStart) event.preventDefault();
    });
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      window.addEventListener("pointerup", (event) => finishDrawing(event.clientX, event.clientY, event.pointerId));
      window.addEventListener("mouseup", (event) => finishDrawing(event.clientX, event.clientY, -1));
      window.addEventListener("keydown", (event) => {
        const target = event.target as HTMLElement | null;
        if (target?.matches("input, textarea, select") || target?.isContentEditable) return;
        if ((event.key === "Delete" || event.key === "Backspace") && this.selectedDrawingId) {
          event.preventDefault();
          this.removeSelectedDrawing();
        }
      });
    }

    this.canvas.addEventListener("mouseleave", () => {
      if (!this.drawingStart) this.crosshair = null;
    });
  }

  public setChartType(type: ChartType) {
    this.chartType = type;
  }

  public setTimeframe(timeframe: string) {
    const match = /^(\d+)(s|m|h)$/.exec(timeframe);
    if (!match) return;
    const unit = match[2] === "h" ? 3600 : match[2] === "m" ? 60 : 1;
    const seconds = Math.max(1, Number(match[1]) * unit);
    if (seconds !== this.timeframeSeconds) this.setCandles([]);
    this.timeframeSeconds = seconds;
    this.immediateScale = true;
  }

  public setInstrument(symbol: string, decimals?: number) {
    if (symbol !== this.instrumentSymbol) {
      this.isAwaitingHistory = true;
      this.setCandles([]);
      this.crosshair = null;
      this.priceScaleZoom = 1;
      this.priceScaleOffset = 0;
      this.panOffsetX = 0;
      this.autoScroll = true;
      this.renderedScaleMin = Number.NaN;
      this.renderedScaleMax = Number.NaN;
      this.scaleAnimationUpdatedAt = 0;
      this.immediateScale = true;
      this.priceAnimationStartedAt = 0;
      this.rolloverAnimationStartedAt = 0;
    }
    this.instrumentSymbol = symbol;
    this.priceDecimals = Number.isInteger(decimals) && Number(decimals) >= 2 && Number(decimals) <= 8
      ? Number(decimals)
      : this.inferPriceDecimals(symbol, this.latestPrice);
  }

  private inferPriceDecimals(symbol: string, price: number): number {
    const normalized = symbol.toUpperCase();
    if (/(JPY)(?:_OTC)?$/.test(normalized)) return 3;
    if (/^(BTC|ETH|XAU|XAG|XPT|XPD|GOLD|USOIL|UKOIL|NGAS|APPLE|NVDA|TSLA|MSFT)/.test(normalized)) return price > 0 && price < 10 ? 4 : 2;
    if (price > 0 && price < 1) return 6;
    return 5;
  }

  private formatPrice(value: number): string {
    if (value === undefined || value === null || isNaN(value)) {
      return "0.00";
    }
    const decimals = this.priceDecimals || this.inferPriceDecimals(this.instrumentSymbol, value);
    return value.toFixed(decimals);
  }

  public setAutoScroll(enabled: boolean) {
    this.autoScroll = enabled;
    if (enabled) this.panOffsetX = 0;
  }

  public applyPan(deltaX: number) {
    this.autoScroll = false;
    this.immediateScale = true;
    const step = this.getCandleStep();
    const plotWidth = Math.max(1, this.width - 65);
    const baseAnchor = plotWidth * this.liveAnchorRatio;
    const maxHistoryPan = Math.max(
      0,
      (this.candles.length - 1) * step - baseAnchor + step * 2,
    );
    this.panOffsetX = Math.max(
      -plotWidth * 0.18,
      Math.min(maxHistoryPan, this.panOffsetX + deltaX),
    );
  }

  public applyZoom(zoomFactor: number, anchorX?: number) {
    if (!Number.isFinite(zoomFactor) || zoomFactor <= 0) return;
    const oldStep = this.getCandleStep();
    const oldZoom = this.zoomScale;
    this.zoomScale = Math.max(
      0.38,
      Math.min(3.4, this.zoomScale * zoomFactor),
    );
    if (this.zoomScale === oldZoom) return;

    const newStep = this.getCandleStep();
    const scaleRatio = newStep / oldStep;
    const plotWidth = Math.max(1, this.width - 65);
    const baseAnchor = plotWidth * this.liveAnchorRatio;

    if (this.autoScroll) {
      // In live follow mode, keep the current forming candle securely in the middle of the terminal
      this.panOffsetX = 0;
    } else if (anchorX !== undefined && Number.isFinite(anchorX)) {
      const dist = (baseAnchor + this.panOffsetX) - anchorX;
      this.panOffsetX += dist * (scaleRatio - 1);
    }
    this.immediateScale = true;
    this.clampPanOffset();
  }

  public applyPriceScale(zoomFactor: number) {
    if (!Number.isFinite(zoomFactor) || zoomFactor <= 0) return;
    this.priceScaleZoom = Math.max(0.45, Math.min(4, this.priceScaleZoom * zoomFactor));
    this.immediateScale = true;
  }

  private startPanInertia() {
    if (Math.abs(this.panVelocity) < 0.04) return;
    let previous = performance.now();
    const step = (now: number) => {
      const elapsed = Math.min(32, Math.max(1, now - previous));
      previous = now;
      this.applyPan(this.panVelocity * elapsed);
      this.panVelocity *= Math.pow(0.9, elapsed / 16.67);
      if (Math.abs(this.panVelocity) >= 0.015) this.inertiaFrameId = requestAnimationFrame(step);
      else this.inertiaFrameId = null;
    };
    this.inertiaFrameId = requestAnimationFrame(step);
  }

  public resetView() {
    this.zoomScale = 1.3;
    this.panOffsetX = 0;
    this.priceScaleZoom = 1;
    this.priceScaleOffset = 0;
    this.autoScroll = true;
    this.crosshair = null;
    this.immediateScale = true;
  }

  public getViewportState() {
    return {
      zoomScale: this.zoomScale,
      panOffsetX: this.panOffsetX,
      followLive: this.autoScroll,
      priceScaleZoom: this.priceScaleZoom,
      priceScaleOffset: this.priceScaleOffset,
    };
  }

  private getCandleStep(): number {
    return Math.max(7, Math.min(58, 24 * this.zoomScale));
  }

  private monotonicNow(): number {
    return typeof performance !== "undefined" ? performance.now() : Date.now();
  }

  private advanceRenderedPrice(now: number) {
    if (!this.latestPrice) return;
    if (!this.renderedPrice || !this.priceAnimationStartedAt) {
      this.renderedPrice = this.latestPrice;
      this.priceAnimationFrom = this.latestPrice;
      return;
    }
    const progress = Math.max(0, Math.min(1,
      (now - this.priceAnimationStartedAt) / this.priceAnimationDurationMs,
    ));
    const eased = progress * progress * (3 - 2 * progress);
    this.renderedPrice = this.priceAnimationFrom
      + (this.latestPrice - this.priceAnimationFrom) * eased;
    if (progress >= 1) {
      this.renderedPrice = this.latestPrice;
      this.priceAnimationFrom = this.latestPrice;
    }
  }

  private getRolloverOffset(step: number, now: number): number {
    if (!this.rolloverAnimationStartedAt) return 0;
    const progress = Math.max(0, Math.min(1,
      (now - this.rolloverAnimationStartedAt) / this.rolloverAnimationDurationMs,
    ));
    if (progress >= 1) {
      this.rolloverAnimationStartedAt = 0;
      return 0;
    }
    return step * (1 - progress);
  }

  private formatGapDuration(seconds: number): string {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return mins > 0 ? `${hours}h ${mins}m gap` : `${hours}h gap`;
    }
    const mins = Math.floor(seconds / 60);
    return `${mins}m gap`;
  }

  private smoothScaleBounds(targetMin: number, targetMax: number, now: number): [number, number] {
    if (
      this.immediateScale ||
      !Number.isFinite(this.renderedScaleMin) ||
      !Number.isFinite(this.renderedScaleMax)
    ) {
      this.renderedScaleMin = targetMin;
      this.renderedScaleMax = targetMax;
      this.scaleAnimationUpdatedAt = now;
      this.immediateScale = false;
      return [targetMin, targetMax];
    }
    const elapsed = Math.max(1, Math.min(100, now - this.scaleAnimationUpdatedAt || 16.67));
    this.scaleAnimationUpdatedAt = now;
    const span = Math.max(this.renderedScaleMax - this.renderedScaleMin, Number.EPSILON);
    const deadZone = span * 0.01;
    const alpha = 1 - Math.exp(-elapsed / 60);

    if (Math.abs(targetMin - this.renderedScaleMin) > deadZone) {
      this.renderedScaleMin += (targetMin - this.renderedScaleMin) * alpha;
    } else {
      this.renderedScaleMin = targetMin;
    }
    if (Math.abs(targetMax - this.renderedScaleMax) > deadZone) {
      this.renderedScaleMax += (targetMax - this.renderedScaleMax) * alpha;
    } else {
      this.renderedScaleMax = targetMax;
    }
    this.renderedScaleMin = Math.min(this.renderedScaleMin, targetMin);
    this.renderedScaleMax = Math.max(this.renderedScaleMax, targetMax);
    return [this.renderedScaleMin, this.renderedScaleMax];
  }

  private clampPanOffset() {
    const step = this.getCandleStep();
    const plotWidth = Math.max(1, this.width - 65);
    const baseAnchor = plotWidth * this.liveAnchorRatio;
    const maxHistoryPan = Math.max(
      0,
      (this.candles.length - 1) * step - baseAnchor + step * 2,
    );
    this.panOffsetX = Math.max(
      -plotWidth * 0.18,
      Math.min(maxHistoryPan, this.panOffsetX),
    );
  }

  public setVolumeVisible(enabled: boolean) {
    this.volumeVisible = enabled;
    this.draw();
  }

  public getVolumeVisible(): boolean {
    return this.volumeVisible;
  }

  public computeDisplayVolume(
    c: Candle,
    refAvgRange?: number,
    refAvgBody?: number,
  ): number {
    const range = Math.max(1e-6, c.high - c.low);
    const body = Math.abs(c.close - c.open);
    const avgR = refAvgRange && refAvgRange > 0 ? refAvgRange : range;
    const avgB = refAvgBody && refAvgBody > 0 ? refAvgBody : body;
    const rangeRatio = range / avgR;
    const bodyRatio = body / Math.max(1e-6, avgB);
    const wick = (c.high - Math.max(c.open, c.close)) + (Math.min(c.open, c.close) - c.low);
    const wickRatio = wick / avgR;
    const seed = Math.sin(c.time * 12.9898 + 78.233) * 43758.5453;
    const jitter = 0.88 + 0.24 * (seed - Math.floor(seed));
    const mult = (
      0.35 +
      Math.min(2.2, rangeRatio * 0.55) +
      Math.min(1.2, bodyRatio * 0.35) +
      Math.min(0.8, wickRatio * 0.25)
    ) * jitter;
    return Math.round(1250 * mult);
  }

  public setDrawingTool(tool: DrawingTool) {
    const validTools: DrawingTool[] = [
      "CURSOR",
      "TREND_LINE",
      "RAY",
      "HORIZONTAL_LINE",
      "VERTICAL_LINE",
      "RECTANGLE",
      "ELLIPSE",
      "TRIANGLE",
      "ARROW",
      "CHANNEL",
      "PARALLEL_CHANNEL",
      "FIBONACCI",
      "FIBONACCI_FAN",
    ];
    this.drawingTool = validTools.includes(tool) ? tool : "CURSOR";
    this.canvas.style.cursor = this.drawingTool === "CURSOR" ? "crosshair" : "cell";
    this.drawingStart = null;
    this.drawingPreview = null;
    this.activePointerId = null;
    this.draw();
  }

  public getDrawingTool(): DrawingTool {
    return this.drawingTool;
  }

  public setDrawingColor(color: string) {
    if (/^#[0-9a-f]{6}$/i.test(color)) {
      this.drawingColor = color;
      this.draw();
    }
  }

  public getDrawingColor(): string {
    return this.drawingColor;
  }

  public clearDrawings() {
    this.drawings = [];
    this.drawingStart = null;
    this.drawingPreview = null;
    this.activePointerId = null;
    this.selectedDrawingId = null;
    this.emitDrawingsChanged();
    this.draw();
  }

  public undoDrawing() {
    const removed = this.drawings.pop();
    if (removed?.id === this.selectedDrawingId) this.selectedDrawingId = null;
    this.emitDrawingsChanged();
    this.draw();
  }

  public getDrawings(): ChartDrawing[] {
    return this.drawings.map((drawing) => ({
      ...drawing,
      start: { ...drawing.start },
      end: { ...drawing.end },
    }));
  }

  public setDrawings(drawings: unknown, notify: boolean = false) {
    const validTools = new Set<DrawingTool>([
      "TREND_LINE", "RAY", "HORIZONTAL_LINE", "VERTICAL_LINE", "RECTANGLE",
      "ELLIPSE", "TRIANGLE", "ARROW", "CHANNEL", "PARALLEL_CHANNEL",
      "FIBONACCI", "FIBONACCI_FAN",
    ]);
    this.drawings = (Array.isArray(drawings) ? drawings : []).flatMap((raw: any) => {
      const finitePoint = (point: any) => point && Number.isFinite(Number(point.x)) && Number.isFinite(Number(point.y));
      if (!validTools.has(raw?.tool) || !finitePoint(raw?.start) || !finitePoint(raw?.end)) return [];
      const clamp = (value: any) => Math.max(0, Math.min(1, Number(value)));
      return [{
        id: typeof raw.id === "string" && raw.id ? raw.id : `DRAW-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        tool: raw.tool as Exclude<DrawingTool, "CURSOR">,
        color: /^#[0-9a-f]{6}$/i.test(String(raw.color || "")) ? String(raw.color) : "#f8fafc",
        start: { x: clamp(raw.start.x), y: clamp(raw.start.y) },
        end: { x: clamp(raw.end.x), y: clamp(raw.end.y) },
      }];
    });
    this.selectedDrawingId = null;
    if (notify) this.emitDrawingsChanged();
    this.draw();
  }

  public getSelectedDrawingId(): string | null {
    return this.selectedDrawingId;
  }

  public removeSelectedDrawing(): boolean {
    if (!this.selectedDrawingId) return false;
    const before = this.drawings.length;
    this.drawings = this.drawings.filter((drawing) => drawing.id !== this.selectedDrawingId);
    this.selectedDrawingId = null;
    if (this.drawings.length !== before) this.emitDrawingsChanged();
    this.draw();
    return this.drawings.length !== before;
  }

  public removeDrawingById(drawingId: string): boolean {
    if (!drawingId) return false;
    const before = this.drawings.length;
    this.drawings = this.drawings.filter((drawing) => drawing.id !== drawingId);
    if (this.selectedDrawingId === drawingId) this.selectedDrawingId = null;
    if (this.drawings.length !== before) this.emitDrawingsChanged();
    this.draw();
    return this.drawings.length !== before;
  }

  public onDrawingsChanged(listener: (drawings: ChartDrawing[]) => void): () => void {
    this.drawingsChangedListeners.add(listener);
    return () => this.drawingsChangedListeners.delete(listener);
  }

  private emitDrawingsChanged() {
    const drawings = this.getDrawings();
    this.drawingsChangedListeners.forEach((listener) => listener(drawings));
  }

  private findDrawingAt(point: { x: number; y: number }): ChartDrawing | null {
    const threshold = 0.035;
    for (let index = this.drawings.length - 1; index >= 0; index--) {
      const drawing = this.drawings[index];
      const minX = Math.min(drawing.start.x, drawing.end.x) - threshold;
      const maxX = Math.max(drawing.start.x, drawing.end.x) + threshold;
      const minY = Math.min(drawing.start.y, drawing.end.y) - threshold;
      const maxY = Math.max(drawing.start.y, drawing.end.y) + threshold;
      if (drawing.tool === "HORIZONTAL_LINE" && Math.abs(point.y - drawing.start.y) <= threshold) return drawing;
      if (drawing.tool === "VERTICAL_LINE" && Math.abs(point.x - drawing.start.x) <= threshold) return drawing;
      if (point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY) return drawing;
    }
    return null;
  }

  public getDrawingCount(): number {
    return this.drawings.length;
  }

  public setDegraded(degraded: boolean) {
    this.isDegraded = degraded;
  }

  public setTradeOverlay(
    trade: {
      strikePrice: number;
      direction: "CALL" | "PUT";
      stake: string;
      payoutPercentage: string;
      expiryTime: number;
      entryTime?: number;
      kind?: "ACTIVE" | "PENDING";
      label?: string;
    } | null,
  ) {
    this.tradeOverlays = trade ? [trade] : [];
  }

  public setTradeOverlays(
    trades: Array<{
      strikePrice: number;
      direction: "CALL" | "PUT";
      stake: string;
      payoutPercentage: string;
      expiryTime: number;
      entryTime?: number;
      kind?: "ACTIVE" | "PENDING";
      label?: string;
    }>,
  ) {
    this.tradeOverlays = (Array.isArray(trades) ? trades : []).filter(
      (t) => t && typeof t.strikePrice === "number" && !isNaN(t.strikePrice) && t.strikePrice > 0
    );
  }

  public setCandles(candles: Candle[]) {
    if (this.frozenForSnapshot) return;
    if (candles.length === 0) {
      if (this.inertiaFrameId !== null) cancelAnimationFrame(this.inertiaFrameId);
      this.inertiaFrameId = null;
      this.panVelocity = 0;
      this.candleHistory.clear();
      this.latestTickTimestampMs = 0;
      this.latestPrice = this.renderedPrice = this.priceAnimationFrom = 0;
      this.priceAnimationStartedAt = this.rolloverAnimationStartedAt = 0;
      this.renderedScaleMin = this.renderedScaleMax = Number.NaN;
      this.scaleAnimationUpdatedAt = 0;
      this.immediateScale = true;
    }
    const byTime = new Map<number, Candle>();
    [...candles].sort((a, b) => a.time - b.time).forEach((candle) => {
      if (![candle.time, candle.open, candle.high, candle.low, candle.close].every(Number.isFinite)
        || Math.min(candle.open, candle.high, candle.low, candle.close) <= 0) return;
      byTime.set(candle.time, {
        ...candle,
        high: Math.max(candle.high, candle.open, candle.close),
        low: Math.min(candle.low, candle.open, candle.close),
      });
    });
    this.candles = [...byTime.values()].slice(-500);

    if (this.candles.length > 0) {
      this.isAwaitingHistory = false;
      this.latestPrice = this.candles[this.candles.length - 1].close;
      this.renderedPrice = this.latestPrice;
      this.priceAnimationFrom = this.latestPrice;
      this.priceAnimationStartedAt = 0;
      this.rolloverAnimationStartedAt = 0;
      this.renderedScaleMin = Number.NaN;
      this.renderedScaleMax = Number.NaN;
      this.scaleAnimationUpdatedAt = 0;
      this.immediateScale = true;
    }
  }

  public getLatestPrice(): number {
    return (
      this.latestPrice ||
      (this.candles.length > 0
        ? this.candles[this.candles.length - 1].close
        : 0)
    );
  }

  public getHistoryRevision(): number { return this.candleHistory.revision; }

  public mergeHistory(candles: Candle[], requestedAt = 0) {
    const now = this.monotonicNow();
    this.advanceRenderedPrice(now);
    this.setCandles(this.candleHistory.merge(candles, requestedAt));
    this.renderedScaleMin = Number.NaN;
    this.renderedScaleMax = Number.NaN;
    this.immediateScale = true;
    this.priceAnimationStartedAt = 0;
    this.rolloverAnimationStartedAt = 0;
  }

  public updateLatestTick(tick: MarketTick) {
    if (this.frozenForSnapshot) return;
    const tickTimestampMs = new Date(tick.tick_timestamp).getTime();
    if (!Number.isFinite(tickTimestampMs) || !Number.isFinite(tick.price) || tick.price <= 0
      || (tick.symbol && this.instrumentSymbol && tick.symbol !== this.instrumentSymbol)
      || (this.latestTickTimestampMs > 0 && tickTimestampMs <= this.latestTickTimestampMs)) return;
    const incomingBucket = Math.floor(tickTimestampMs / 1000 / this.timeframeSeconds) * this.timeframeSeconds;
    if (this.candles.length && incomingBucket < this.candles[this.candles.length - 1].time) return;
    this.latestTickTimestampMs = tickTimestampMs;
    const animationNow = this.monotonicNow();
    this.advanceRenderedPrice(animationNow);
    this.priceAnimationFrom = this.renderedPrice || tick.price;
    this.priceAnimationStartedAt = animationNow;
    this.latestPrice = tick.price;
    if (!this.renderedPrice) this.renderedPrice = tick.price;
    const tickEpoch = Math.floor(
      new Date(tick.tick_timestamp).getTime() / 1000,
    );
    if (!Number.isFinite(tickEpoch) || !Number.isFinite(tick.price)) return;
    const bucketTime =
      Math.floor(tickEpoch / this.timeframeSeconds) * this.timeframeSeconds;
    if (this.candles.length === 0) {
      if (this.isAwaitingHistory) {
        this.latestPrice = tick.price;
        this.renderedPrice = tick.price;
        return;
      }
      this.candles.push({
        time: bucketTime,
        open: tick.price,
        high: tick.price,
        low: tick.price,
        close: tick.price,
        volume: 1,
      });
      this.immediateScale = true;
      this.candleHistory.record(this.candles[this.candles.length - 1]);
      return;
    }

    const lastCandle = this.candles[this.candles.length - 1];
    if (bucketTime > lastCandle.time) {
      this.rolloverAnimationStartedAt = animationNow;
      const isConsecutive = bucketTime <= lastCandle.time + this.timeframeSeconds;
      if (!isConsecutive && bucketTime > lastCandle.time + this.timeframeSeconds * 3) {
        this.onGapDetectedCallback?.();
      }
      // Match server aggregation: a bucket opens at its first actual tick.
      // Using the previous close here changes OHLC after a history refresh.
      const openPrice = tick.price;
      const highPrice = tick.price;
      const lowPrice = tick.price;
      if (!isConsecutive) {
        this.immediateScale = true;
      }
      this.candles.push({
        time: bucketTime,
        open: openPrice,
        high: highPrice,
        low: lowPrice,
        close: tick.price,
        volume: 1,
      });
      if (this.candles.length > 500)
        this.candles.splice(0, this.candles.length - 500);
      this.candleHistory.record(this.candles[this.candles.length - 1]);
      return;
    }
    if (bucketTime < lastCandle.time) return;
    lastCandle.high = Math.max(lastCandle.high, tick.price);
    lastCandle.low = Math.min(lastCandle.low, tick.price);
    lastCandle.close = tick.price;
    const priceDelta = Math.abs(tick.price - lastCandle.close);
    const isNewExtremum = tick.price > lastCandle.high || tick.price < lastCandle.low;
    const volIncrement = 1 + (isNewExtremum ? 4 : 0) + (priceDelta > 0 ? 1 : 0);
    lastCandle.volume = (lastCandle.volume || 0) + volIncrement;
    this.candleHistory.record(lastCandle);
  }

  private startRenderLoop() {
    const render = () => {
      try {
        this.draw();
      } catch (err) {
        console.error("[CanvasChart] Draw loop error:", err);
      } finally {
        this.animationFrameId = requestAnimationFrame(render);
      }
    };
    this.animationFrameId = requestAnimationFrame(render);
  }

  public draw() {
    const wall = Date.now();
    if (this.lastDrawWall && (wall - this.lastDrawWall > 60000 || wall < this.lastDrawWall) && !this.recovering) {
      this.onResumeDetected?.();
    }
    this.lastDrawWall = wall;
    const { width, height, ctx } = this;
    if (width <= 0 || height <= 0) return;

    // 1. Background
    const currentTheme = (typeof document !== "undefined" && document.documentElement?.getAttribute("data-theme")) || "dark";
    if (currentTheme === "light") {
      ctx.fillStyle = "#ffffff";
    } else if (currentTheme === "twilight") {
      ctx.fillStyle = "#1a1e2e";
    } else {
      ctx.fillStyle = "#0b0e14";
    }
    ctx.fillRect(0, 0, width, height);

    if (this.recovering && this.candles.length === 0) {
      ctx.fillStyle = '#94a3b8'; ctx.font = '14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('Resynchronizing market history…', width / 2, height / 2);
      return;
    }
    if (this.candles.length === 0) {
      ctx.fillStyle = currentTheme === "light" ? "#334155" : "#64748b";
      ctx.font = "14px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(
        this.isAwaitingHistory ? "Loading market history…" : "Awaiting authoritative market feed ticks...",
        width / 2,
        height / 2,
      );
      return;
    }

    // Animate by elapsed time rather than frame count so 60 Hz and 120 Hz
    // displays show the same calm, nearly full-tick movement duration.
    const frameNow = this.monotonicNow();
    this.advanceRenderedPrice(frameNow);
    if (this.autoScroll) this.panOffsetX = 0;
    this.clampPanOffset();
    const chartRightPadding = 65;
    const chartBottomPadding = 30;
    const plotWidth = width - chartRightPadding;
    const plotHeight = height - chartBottomPadding;
    const candleSpacing = this.getCandleStep();
    const baseAnchor = plotWidth * this.liveAnchorRatio;
    const liveAnchorX = baseAnchor + this.panOffsetX;
    const rolloverOffset = this.getRolloverOffset(candleSpacing, frameNow);
    const xForCandleIndex = (index: number) => {
      const distanceFromLive = this.candles.length - 1 - index;
      if (distanceFromLive === 0) return liveAnchorX;
      return liveAnchorX - distanceFromLive * candleSpacing + rolloverOffset;
    };
    const visibleIndexes = this.candles
      .map((_candle, index) => index)
      .filter((index) => {
        const x = xForCandleIndex(index);
        return x >= -candleSpacing && x <= plotWidth + candleSpacing;
      });
    const firstVisibleIndex = visibleIndexes[0] ?? this.candles.length - 1;
    const lastVisibleIndex =
      visibleIndexes[visibleIndexes.length - 1] ?? this.candles.length - 1;
    const visibleCandles = this.candles.slice(
      firstVisibleIndex,
      lastVisibleIndex + 1,
    );
    if (visibleCandles.length === 0) {
      visibleCandles.push(this.candles[this.candles.length - 1]);
    }

    // 2. Compute Dynamic Price Scale Bounds
    // Scale the same visible candles we draw, including recorded history before gaps.
    const isLiveViewing = this.autoScroll || this.panOffsetX <= candleSpacing * 3;
    const candlesForScale = visibleCandles.length ? visibleCandles : this.candles;

    let minPrice = Infinity;
    let maxPrice = -Infinity;
    for (const c of candlesForScale) {
      if (c.low < minPrice) minPrice = c.low;
      if (c.high > maxPrice) maxPrice = c.high;
    }
    if (isLiveViewing && this.latestPrice > 0) {
      minPrice = Math.min(minPrice, this.latestPrice, this.renderedPrice || this.latestPrice);
      maxPrice = Math.max(maxPrice, this.latestPrice, this.renderedPrice || this.latestPrice);
    }
    if (minPrice === maxPrice || !Number.isFinite(minPrice) || !Number.isFinite(maxPrice)) {
      minPrice = (this.latestPrice || 1) * 0.9995;
      maxPrice = (this.latestPrice || 1) * 1.0005;
    }
    const pricePadding = (maxPrice - minPrice) * 0.08 || Math.max(this.latestPrice * 0.0005, 0.0005);
    [minPrice, maxPrice] = this.smoothScaleBounds(
      minPrice - pricePadding,
      maxPrice + pricePadding,
      frameNow,
    );
    const naturalMid = (minPrice + maxPrice) / 2;
    const naturalRange = maxPrice - minPrice;
    const priceRange = naturalRange / this.priceScaleZoom;
    const shiftedMid = naturalMid + this.priceScaleOffset * naturalRange;
    minPrice = shiftedMid - priceRange / 2;
    maxPrice = shiftedMid + priceRange / 2;

    const priceToY = (price: number) => {
      return plotHeight - ((price - minPrice) / priceRange) * plotHeight;
    };

    // 3. Grid Lines & Price Axis
    ctx.strokeStyle = currentTheme === "light" 
      ? "rgba(0, 0, 0, 0.06)" 
      : currentTheme === "twilight" 
      ? "rgba(255, 255, 255, 0.08)" 
      : "rgba(255, 255, 255, 0.05)";
    ctx.lineWidth = 1;
    ctx.font = '11px "JetBrains Mono", monospace';
    ctx.fillStyle = currentTheme === "light" ? "#334155" : "#64748b";
    ctx.textAlign = "left";

    const numGridLines = 6;
    for (let i = 0; i <= numGridLines; i++) {
      const y = (plotHeight / numGridLines) * i;
      const priceVal = maxPrice - (priceRange / numGridLines) * i;

      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(plotWidth, y);
      ctx.stroke();

      ctx.fillText(this.formatPrice(priceVal), plotWidth + 6, y + 4);
    }

    // Subtle time grid: StarX background cells without heavy filled
    // boxes. Keep them quiet so candles and drawings remain the focal layer.
    const verticalGridStep = Math.max(1, Math.ceil(92 / candleSpacing));
    const gridStartIndex = Math.floor(firstVisibleIndex / verticalGridStep) * verticalGridStep;
    ctx.save();
    ctx.strokeStyle = currentTheme === "light"
      ? "rgba(15, 23, 42, 0.055)"
      : "rgba(148, 163, 184, 0.07)";
    ctx.lineWidth = 1;
    for (let index = gridStartIndex; index < this.candles.length; index += verticalGridStep) {
      const x = xForCandleIndex(index);
      if (x < 0 || x > plotWidth) continue;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, plotHeight);
      ctx.stroke();
    }
    for (let future = verticalGridStep; liveAnchorX + future * candleSpacing <= plotWidth; future += verticalGridStep) {
      const x = liveAnchorX + future * candleSpacing;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, plotHeight);
      ctx.stroke();
    }
    ctx.restore();

    ctx.save(); ctx.beginPath(); ctx.rect(0, 0, plotWidth, plotHeight); ctx.clip();
    // 4. Render Candles / Line
    const candleCount = visibleCandles.length;
    // Match the readable QX/PO market density: a body occupies most of its
    // time slot, with a small gutter and a firm viewport-safe width clamp.
    const candleWidth = Math.max(4, Math.min(44, candleSpacing * 0.72));
    const xForVisibleIndex = (index: number) =>
      xForCandleIndex(firstVisibleIndex + index);

    if (this.chartType === "CANDLESTICK") {
      for (let i = 0; i < candleCount; i++) {
        const source = visibleCandles[i];
        const isFormingCandle = firstVisibleIndex + i === this.candles.length - 1;
        const c = isFormingCandle
          ? { ...source, close: this.renderedPrice }
          : source;
        const x = xForVisibleIndex(i);
        const isUp = c.close >= c.open;
        const color = isUp ? "#10b981" : "#f43f5e";

        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = Math.max(1.2, Math.min(2, candleWidth * 0.08));

        // Wick
        const highY = priceToY(c.high);
        const lowY = priceToY(c.low);
        ctx.beginPath();
        ctx.moveTo(x, highY);
        ctx.lineTo(x, lowY);
        ctx.stroke();

        // Body
        const openY = priceToY(c.open);
        const closeY = priceToY(c.close);
        const bodyTop = Math.min(openY, closeY);
        const bodyHeight = Math.max(3, Math.abs(closeY - openY));

        ctx.fillRect(x - candleWidth / 2, bodyTop, candleWidth, bodyHeight);
      }
    } else if (this.chartType === "BARS") {
      for (let i = 0; i < candleCount; i++) {
        const c = visibleCandles[i];
        const x = xForVisibleIndex(i);
        const color = c.close >= c.open ? "#10b981" : "#f43f5e";
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(x, priceToY(c.high));
        ctx.lineTo(x, priceToY(c.low));
        ctx.moveTo(x - candleWidth / 2, priceToY(c.open));
        ctx.lineTo(x, priceToY(c.open));
        ctx.moveTo(x, priceToY(c.close));
        ctx.lineTo(x + candleWidth / 2, priceToY(c.close));
        ctx.stroke();
      }
    } else {
      // Line / Area Chart Mode
      ctx.strokeStyle = "#38bdf8";
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < candleCount; i++) {
        const c = visibleCandles[i];
        const x = xForVisibleIndex(i);
        const isFormingCandle = firstVisibleIndex + i === this.candles.length - 1;
        const y = priceToY(isFormingCandle ? this.renderedPrice : c.close);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      if (this.chartType === "AREA") {
        ctx.lineTo(xForVisibleIndex(candleCount - 1), plotHeight);
        ctx.lineTo(xForVisibleIndex(0), plotHeight);
        ctx.closePath();
        const area = ctx.createLinearGradient(0, 0, 0, plotHeight);
        area.addColorStop(0, "rgba(56,189,248,.28)");
        area.addColorStop(1, "rgba(56,189,248,.01)");
        ctx.fillStyle = area;
        ctx.fill();
      }
    }

    // 4A. Authoritative candle-volume histogram and UTC time axis.
    if (this.volumeVisible && visibleCandles.length > 0) {
      let totalRange = 0;
      let totalBody = 0;
      visibleCandles.forEach((c) => {
        totalRange += Math.max(1e-6, c.high - c.low);
        totalBody += Math.abs(c.close - c.open);
      });
      const avgRange = (totalRange / visibleCandles.length) || 1e-4;
      const avgBody = (totalBody / visibleCandles.length) || 1e-4;

      const volumes = visibleCandles.map((c) =>
        this.computeDisplayVolume(c, avgRange, avgBody)
      );

      const minVol = Math.min(...volumes);
      const maxVol = Math.max(1, ...volumes);
      const volDelta = maxVol - minVol;

      // Prominent institutional volume histogram height: 50px to 105px
      const volumeHeight = Math.max(50, Math.min(105, plotHeight * 0.22));
      const barWidth = Math.max(1.5, Math.min(candleWidth * 0.88, candleSpacing - 2));

      visibleCandles.forEach((c, i) => {
        const vol = volumes[i];
        // Dynamic contrast: minimum 16% height, linear contrast to 100%
        const normalized = volDelta > 0 ? (vol - minVol) / volDelta : 0.5;
        const h = Math.max(12, Math.min(volumeHeight, (0.16 + 0.84 * normalized) * volumeHeight));
        const isBullish = c.close >= c.open;
        const xCenter = xForVisibleIndex(i);
        const xLeft = xCenter - barWidth / 2;
        const yTop = plotHeight - h;

        // Base bar fill with vibrant opacity
        ctx.fillStyle = isBullish ? "rgba(16, 185, 129, 0.60)" : "rgba(244, 63, 94, 0.60)";
        ctx.fillRect(xLeft, yTop, barWidth, h);

        // Crisp 1.5px top highlight cap for sharp, institutional look
        ctx.fillStyle = isBullish ? "rgba(52, 211, 153, 1.0)" : "rgba(251, 113, 133, 1.0)";
        ctx.fillRect(xLeft, yTop, barWidth, Math.min(1.5, h));
      });
    }
    ctx.restore();
    ctx.fillStyle = currentTheme === "light" ? "#475569" : "#64748b";
    ctx.font = '10px "JetBrains Mono", monospace';
    ctx.textAlign = "center";
    const timeStep = Math.max(1, Math.ceil(84 / candleSpacing));
    let lastTimeLabelX = -Infinity;
    visibleCandles.forEach((c, i) => {
      if (i % timeStep !== 0 && i !== candleCount - 1) return;
      const time = new Date(c.time * 1000).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: this.timeframeSeconds < 60 ? "2-digit" : undefined,
        hour12: false,
        timeZone: this.chartTimezone || "UTC", // timeZone: "UTC"
      });
      const x = xForVisibleIndex(i);
      if (x - lastTimeLabelX < 58) return;
      ctx.fillText(time, x, height - 8);
      lastTimeLabelX = x;
    });
    ctx.textAlign = "left";

    ctx.save(); ctx.beginPath(); ctx.rect(0, 0, plotWidth, plotHeight); ctx.clip();
    // 4B. Authoritative indicator overlays and oscillator pane (21 selectable studies)
    const drawPriceSeries = (
      values: Array<number | null>,
      color: string,
      widthPx: number = 1.35,
      dashed: boolean = false,
    ) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = widthPx;
      ctx.setLineDash(dashed ? [4, 3] : []);
      ctx.beginPath();
      let started = false;
      values.forEach((value, index) => {
        if (value === null || !Number.isFinite(value)) {
          started = false;
          return;
        }
        const x = xForVisibleIndex(index);
        const y = priceToY(value);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.setLineDash([]);
    };
    const valueSeries = (points: Array<{ value: number | null }>) =>
      points.map((point) => point.value);
    const oscillatorSeries: Array<{
      name: string;
      color: string;
      values: Array<number | null>;
    }> = [];

    if (this.activeIndicators.has("SMA"))
      drawPriceSeries(
        valueSeries(calculateSMA(visibleCandles, Math.min(20, candleCount))),
        "#f59e0b",
        1.6,
      );
    if (this.activeIndicators.has("EMA"))
      drawPriceSeries(
        valueSeries(calculateEMA(visibleCandles, Math.min(20, candleCount))),
        "#22d3ee",
      );
    if (this.activeIndicators.has("WMA"))
      drawPriceSeries(
        valueSeries(calculateWMA(visibleCandles, Math.min(20, candleCount))),
        "#a78bfa",
      );
    if (this.activeIndicators.has("HMA"))
      drawPriceSeries(
        valueSeries(calculateHullMA(visibleCandles, Math.min(20, candleCount))),
        "#fb7185",
      );
    if (this.activeIndicators.has("SUPERTREND"))
      drawPriceSeries(
        calculateSupertrend(visibleCandles).map((point) => point.value),
        "#34d399",
        1.8,
      );
    if (this.activeIndicators.has("PSAR"))
      drawPriceSeries(
        valueSeries(calculateParabolicSAR(visibleCandles)),
        "#f472b6",
        1.1,
        true,
      );
    if (this.activeIndicators.has("ZIGZAG"))
      drawPriceSeries(
        valueSeries(calculateZigZag(visibleCandles)),
        "#facc15",
        1.5,
      );
    if (this.activeIndicators.has("ICHIMOKU")) {
      const points = calculateIchimoku(visibleCandles);
      drawPriceSeries(
        points.map((point) => point.tenkan),
        "#ef4444",
      );
      drawPriceSeries(
        points.map((point) => point.kijun),
        "#3b82f6",
      );
      drawPriceSeries(
        points.map((point) => point.senkouA),
        "rgba(52,211,153,.75)",
      );
      drawPriceSeries(
        points.map((point) => point.senkouB),
        "rgba(248,113,113,.75)",
      );
    }
    if (this.activeIndicators.has("ALLIGATOR")) {
      const points = calculateAlligator(visibleCandles);
      drawPriceSeries(
        points.map((point) => point.jaw),
        "#60a5fa",
      );
      drawPriceSeries(
        points.map((point) => point.teeth),
        "#ef4444",
      );
      drawPriceSeries(
        points.map((point) => point.lips),
        "#22c55e",
      );
    }
    const drawChannel = (
      points: Array<{
        upper: number | null;
        middle: number | null;
        lower: number | null;
      }>,
      color: string,
    ) => {
      drawPriceSeries(
        points.map((point) => point.upper),
        color,
        1,
        true,
      );
      drawPriceSeries(
        points.map((point) => point.middle),
        color,
        1.2,
      );
      drawPriceSeries(
        points.map((point) => point.lower),
        color,
        1,
        true,
      );
    };
    if (this.activeIndicators.has("BOLLINGER"))
      drawChannel(calculateBollingerBands(visibleCandles), "#818cf8");
    if (this.activeIndicators.has("DONCHIAN"))
      drawChannel(calculateDonchianChannels(visibleCandles), "#2dd4bf");
    if (this.activeIndicators.has("KELTNER"))
      drawChannel(calculateKeltnerChannels(visibleCandles), "#f97316");
    if (this.activeIndicators.has("ENVELOPES"))
      drawChannel(calculateEnvelopes(visibleCandles), "#e879f9");
    if (this.activeIndicators.has("FRACTALS")) {
      const fractals = calculateFractals(visibleCandles);
      ctx.font = "bold 11px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (let i = 0; i < visibleCandles.length; i++) {
        const x = xForVisibleIndex(i);
        if (fractals[i]?.up !== null) {
          const y = priceToY(fractals[i].up!) - 8;
          ctx.fillStyle = "#10b981";
          ctx.fillText("▲", x, y);
        }
        if (fractals[i]?.down !== null) {
          const y = priceToY(fractals[i].down!) + 8;
          ctx.fillStyle = "#ef4444";
          ctx.fillText("▼", x, y);
        }
      }
    }

    if (this.activeIndicators.has("RSI"))
      oscillatorSeries.push({
        name: "RSI",
        color: "#22d3ee",
        values: valueSeries(calculateRSI(visibleCandles)),
      });
    if (this.activeIndicators.has("STOCHASTIC"))
      oscillatorSeries.push({
        name: "STOCH",
        color: "#a78bfa",
        values: calculateStochastic(visibleCandles).map((point) => point.k),
      });
    if (this.activeIndicators.has("MACD"))
      oscillatorSeries.push({
        name: "MACD",
        color: "#34d399",
        values: calculateMACD(visibleCandles).map((point) => point.macd),
      });
    if (this.activeIndicators.has("CCI"))
      oscillatorSeries.push({
        name: "CCI",
        color: "#f59e0b",
        values: valueSeries(calculateCCI(visibleCandles)),
      });
    if (this.activeIndicators.has("WILLIAMS_R"))
      oscillatorSeries.push({
        name: "W%R",
        color: "#fb7185",
        values: valueSeries(calculateWilliamsR(visibleCandles)),
      });
    if (this.activeIndicators.has("AWESOME"))
      oscillatorSeries.push({
        name: "AO",
        color: "#38bdf8",
        values: valueSeries(calculateAwesomeOscillator(visibleCandles)),
      });
    if (this.activeIndicators.has("MOMENTUM"))
      oscillatorSeries.push({
        name: "MOM",
        color: "#e879f9",
        values: valueSeries(calculateMomentum(visibleCandles)),
      });
    if (this.activeIndicators.has("ROC"))
      oscillatorSeries.push({
        name: "ROC",
        color: "#facc15",
        values: valueSeries(calculateROC(visibleCandles)),
      });
    if (this.activeIndicators.has("ATR"))
      oscillatorSeries.push({
        name: "ATR",
        color: "#f97316",
        values: valueSeries(calculateATR(visibleCandles)),
      });
    if (this.activeIndicators.has("ADX")) {
      const adxPoints = calculateADX(visibleCandles);
      oscillatorSeries.push(
        { name: "ADX", color: "#f59e0b", values: adxPoints.map((p) => p.adx) },
        { name: "+DI", color: "#10b981", values: adxPoints.map((p) => p.plusDI) },
        { name: "-DI", color: "#ef4444", values: adxPoints.map((p) => p.minusDI) },
      );
    }
    if (this.activeIndicators.has("AROON")) {
      const aroonPoints = calculateAroon(visibleCandles);
      oscillatorSeries.push(
        { name: "Aroon Up", color: "#10b981", values: aroonPoints.map((p) => p.aroonUp) },
        { name: "Aroon Down", color: "#ef4444", values: aroonPoints.map((p) => p.aroonDown) },
      );
    }
    if (this.activeIndicators.has("VORTEX")) {
      const vortexPoints = calculateVortex(visibleCandles);
      oscillatorSeries.push(
        { name: "VI+", color: "#10b981", values: vortexPoints.map((p) => p.plusVI) },
        { name: "VI-", color: "#a855f7", values: vortexPoints.map((p) => p.minusVI) },
      );
    }
    if (this.activeIndicators.has("BULLS_BEARS")) {
      const elder = calculateElderRay(visibleCandles);
      oscillatorSeries.push(
        { name: "Bulls", color: "#10b981", values: elder.map((p) => p.bullsPower) },
        { name: "Bears", color: "#ef4444", values: elder.map((p) => p.bearsPower) },
      );
    }
    if (this.activeIndicators.has("AC")) {
      const acPoints = calculateAcceleratorOscillator(visibleCandles);
      oscillatorSeries.push({
        name: "AC",
        color: "#38bdf8",
        values: acPoints.map((p) => p.value),
      });
    }
    if (this.activeIndicators.has("DEMARKER")) {
      const dem = calculateDeMarker(visibleCandles);
      oscillatorSeries.push({
        name: "DeM",
        color: "#06b6d4",
        values: dem.map((p) => p.value),
      });
    }

    if (oscillatorSeries.length) {
      const paneHeight = Math.max(58, plotHeight * 0.22);
      const paneTop = plotHeight - paneHeight;
      ctx.fillStyle = currentTheme === "light" ? "rgba(248, 250, 252, 0.95)" : "rgba(7,10,16,.88)";
      ctx.fillRect(0, paneTop, plotWidth, paneHeight);
      ctx.strokeStyle = currentTheme === "light" ? "#cbd5e1" : "#263244";
      ctx.beginPath();
      ctx.moveTo(0, paneTop);
      ctx.lineTo(plotWidth, paneTop);
      ctx.stroke();
      oscillatorSeries.forEach((series, seriesIndex) => {
        const finite = series.values.filter(
          (value): value is number => value !== null && Number.isFinite(value),
        );
        if (!finite.length) return;
        const low = Math.min(...finite);
        const high = Math.max(...finite);
        const range = high - low || 1;
        ctx.strokeStyle = series.color;
        ctx.lineWidth = 1.25;
        ctx.beginPath();
        let started = false;
        series.values.forEach((value, index) => {
          if (value === null || !Number.isFinite(value)) {
            started = false;
            return;
          }
          const x = xForVisibleIndex(index);
          const y =
            paneTop +
            paneHeight -
            8 -
            ((value - low) / range) * (paneHeight - 18);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.fillStyle = series.color;
        ctx.font = '9px "JetBrains Mono", monospace';
        ctx.fillText(
          series.name,
          7 + (seriesIndex % 6) * 52,
          paneTop + 11 + Math.floor(seriesIndex / 6) * 11,
        );
      });
    }

    // 4C. User-authored technical drawings. Coordinates are normalized to
    // the drawable viewport so drawings survive high-DPI and responsive
    // resizing without being confused with market/settlement data.
    const renderDrawing = (drawing: ChartDrawing) => {
      const x1 = drawing.start.x * plotWidth;
      const y1 = drawing.start.y * plotHeight;
      const x2 = drawing.end.x * plotWidth;
      const y2 = drawing.end.y * plotHeight;
      ctx.strokeStyle = drawing.color;
      ctx.fillStyle = `${drawing.color}22`;
      ctx.lineWidth = 1.6;
      ctx.setLineDash([]);
      ctx.beginPath();
      if (drawing.tool === "HORIZONTAL_LINE") {
        ctx.moveTo(0, y1);
        ctx.lineTo(plotWidth, y1);
      } else if (drawing.tool === "VERTICAL_LINE") {
        ctx.moveTo(x1, 0);
        ctx.lineTo(x1, plotHeight);
      } else if (drawing.tool === "RAY") {
        const dx = x2 - x1 || 1;
        const dy = y2 - y1;
        const scale = dx >= 0 ? (plotWidth - x1) / dx : -x1 / dx;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x1 + dx * scale, y1 + dy * scale);
      } else if (drawing.tool === "RECTANGLE") {
        ctx.rect(x1, y1, x2 - x1, y2 - y1);
        ctx.fill();
      } else if (drawing.tool === "ELLIPSE") {
        const centerX = (x1 + x2) / 2;
        const centerY = (y1 + y2) / 2;
        const radiusX = Math.max(2, Math.abs(x2 - x1) / 2);
        const radiusY = Math.max(2, Math.abs(y2 - y1) / 2);
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (drawing.tool === "TRIANGLE") {
        const apexX = (x1 + x2) / 2;
        ctx.moveTo(apexX, Math.min(y1, y2));
        ctx.lineTo(Math.max(x1, x2), Math.max(y1, y2));
        ctx.lineTo(Math.min(x1, x2), Math.max(y1, y2));
        ctx.closePath();
        ctx.fill();
      } else if (drawing.tool === "ARROW") {
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const head = 10;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
      } else if (drawing.tool === "CHANNEL" || drawing.tool === "PARALLEL_CHANNEL") {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const length = Math.max(1, Math.hypot(dx, dy));
        const offset = drawing.tool === "CHANNEL"
          ? Math.max(12, Math.min(42, length * 0.12))
          : Math.max(18, Math.min(64, length * 0.22));
        const nx = (-dy / length) * offset;
        const ny = (dx / length) * offset;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.moveTo(x1 + nx, y1 + ny);
        ctx.lineTo(x2 + nx, y2 + ny);
        if (drawing.tool === "PARALLEL_CHANNEL") {
          ctx.moveTo(x1 - nx, y1 - ny);
          ctx.lineTo(x2 - nx, y2 - ny);
        }
      } else if (drawing.tool === "FIBONACCI") {
        const levels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
        levels.forEach((level) => {
          const y = y1 + (y2 - y1) * level;
          ctx.moveTo(Math.min(x1, x2), y);
          ctx.lineTo(Math.max(x1, x2), y);
        });
      } else if (drawing.tool === "FIBONACCI_FAN") {
        const levels = [0, 0.382, 0.5, 0.618, 1];
        const endX = Math.max(x1 + 1, x2);
        levels.forEach((level) => {
          const endY = y1 + (y2 - y1) * level;
          ctx.moveTo(x1, y1);
          ctx.lineTo(endX, endY);
        });
      } else {
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
      }
      ctx.stroke();
      if (drawing.id === this.selectedDrawingId) {
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = "#ffffff";
        ctx.strokeRect(Math.min(x1, x2) - 5, Math.min(y1, y2) - 5, Math.max(10, Math.abs(x2 - x1) + 10), Math.max(10, Math.abs(y2 - y1) + 10));
        ctx.setLineDash([]);
      }
    };
    this.drawings.forEach(renderDrawing);
    if (this.drawingStart && this.drawingPreview && this.drawingTool !== "CURSOR") {
      renderDrawing({
        id: "DRAWING_PREVIEW",
        tool: this.drawingTool,
        color: this.drawingColor,
        start: this.drawingStart,
        end: this.drawingPreview,
      });
    }

    ctx.restore();
    // 5. Current-candle and authoritative trade-window timing guides.
    // Preview follows the selected duration; an active contract replaces it
    // with its immutable entry and expiry timestamps.
    const nowMs = Date.now();
    const activeTiming = this.tradeOverlays
      .filter((overlay) => overlay.kind === "ACTIVE" && overlay.expiryTime > nowMs)
      .sort((a, b) => a.expiryTime - b.expiryTime)[0];
    const secondsPerSlot = Math.max(1, this.timeframeSeconds);
    const beginningX = activeTiming?.entryTime && Number.isFinite(activeTiming.entryTime)
      ? liveAnchorX - ((nowMs - activeTiming.entryTime) / 1000 / secondsPerSlot) * candleSpacing
      : liveAnchorX;
    const expiryMs = activeTiming?.expiryTime || nowMs + this.tradeWindowDurationSeconds * 1000;
    const endX = liveAnchorX + ((expiryMs - nowMs) / 1000 / secondsPerSlot) * candleSpacing;
    const utcClock = (timestampMs: number) => {
      const str = new Date(timestampMs).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: this.chartTimezone,
      });
      return `${str} ${this.chartTimezoneLabel}`;
    };
    const drawTimingGuide = (x: number, label: string, dashed: boolean, align: CanvasTextAlign, labelY: number = 18, detail: string = "") => {
      if (x < 0 || x > plotWidth) return;
      ctx.save();
      ctx.strokeStyle = currentTheme === "light" ? "rgba(51, 65, 85, 0.48)" : "rgba(226, 232, 240, 0.48)";
      ctx.lineWidth = 1;
      ctx.setLineDash(dashed ? [4, 5] : []);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, plotHeight);
      ctx.stroke();
      ctx.setLineDash([]);

      // Avoid overlapping with .chart-top-overlay (x: 0..540, y: 0..48)
      let effectiveAlign = align;
      if (align === "right" && x < 140) {
        effectiveAlign = "left";
      }
      let safeLabelY = labelY;
      const isUnderTopOverlay = x <= 560 || (effectiveAlign === "right" && x <= 680);
      if (isUnderTopOverlay && safeLabelY < 74) {
        safeLabelY = 74;
      }

      ctx.font = '600 10px "Inter", sans-serif';
      const textWidth = Math.max(ctx.measureText(label).width, detail ? 70 : 0);
      const labelX = effectiveAlign === "right" ? x - 8 : x + 8;
      const badgeX = effectiveAlign === "right" ? labelX - textWidth - 4 : labelX - 4;
      const badgeHeight = detail ? 26 : 15;
      const badgeY = safeLabelY - 10;

      // Render subtle background pill for high contrast and readability
      ctx.fillStyle = currentTheme === "light" ? "rgba(241, 245, 249, 0.88)" : "rgba(15, 23, 42, 0.88)";
      ctx.beginPath();
      if (typeof (ctx as any).roundRect === "function") {
        (ctx as any).roundRect(badgeX, badgeY, textWidth + 8, badgeHeight, 4);
      } else {
        ctx.rect(badgeX, badgeY, textWidth + 8, badgeHeight);
      }
      ctx.fill();

      ctx.fillStyle = currentTheme === "light" ? "rgba(15, 23, 42, 0.9)" : "rgba(226, 232, 240, 0.95)";
      ctx.textAlign = effectiveAlign;
      ctx.fillText(label, labelX, safeLabelY);
      if (detail) {
        ctx.font = '500 9px "JetBrains Mono", monospace';
        ctx.fillStyle = currentTheme === "light" ? "#64748b" : "#94a3b8";
        ctx.fillText(detail, labelX, safeLabelY + 12);
      }
      ctx.fillStyle = currentTheme === "light" ? "#475569" : "#cbd5e1";
      ctx.beginPath();
      ctx.moveTo(x, 2);
      ctx.lineTo(x - 4, 9);
      ctx.lineTo(x + 4, 9);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    // Keep the immutable entry marker visible even during the first seconds,
    // when it is naturally very close to the moving current-candle guide.
    if (activeTiming) {
      const isBegUnder = beginningX <= 560 || beginningX <= 680;
      const begY = isBegUnder ? 74 : 18;
      drawTimingGuide(beginningX, "Beginning of trade", false, "right", begY, utcClock(activeTiming.entryTime || nowMs));
      const candleY = Math.abs(beginningX - liveAnchorX) < 70 ? (begY + 30) : ((liveAnchorX <= 560 || liveAnchorX <= 680) ? 74 : 18);
      drawTimingGuide(liveAnchorX, "Current candle", true, "left", candleY);
    } else {
      const begY = (liveAnchorX <= 560 || liveAnchorX <= 680) ? 74 : 18;
      drawTimingGuide(liveAnchorX, "Beginning of trade", true, "right", begY);
    }
    drawTimingGuide(endX, "End of trade", false, "left", 18, utcClock(expiryMs));
    const additionalExpiries = this.tradeOverlays
      .filter((overlay) => overlay !== activeTiming && overlay.kind === "ACTIVE" && overlay.expiryTime > nowMs)
      .filter((overlay, index, all) => all.findIndex((item) => Math.abs(item.expiryTime - overlay.expiryTime) < 500) === index)
      .slice(0, 4);
    additionalExpiries.forEach((overlay, index) => {
      const overlayX = liveAnchorX + ((overlay.expiryTime - nowMs) / 1000 / secondsPerSlot) * candleSpacing;
      drawTimingGuide(overlayX, `Expiry ${index + 2}`, false, "left", 46 + index * 16, utcClock(overlay.expiryTime));
    });
    if (endX >= 0 && endX <= plotWidth) {
      const remainingSeconds = Math.max(0, Math.ceil((expiryMs - nowMs) / 1000));
      const countdown = `${String(Math.floor(remainingSeconds / 60)).padStart(2, "0")}:${String(remainingSeconds % 60).padStart(2, "0")}`;
      ctx.save();
      ctx.font = '700 10px "JetBrains Mono", monospace';
      const badgeWidth = ctx.measureText(countdown).width + 12;
      const badgeX = Math.min(plotWidth - badgeWidth - 2, Math.max(2, endX - badgeWidth / 2));
      ctx.fillStyle = currentTheme === "light" ? "rgba(51, 65, 85, 0.9)" : "rgba(30, 41, 59, 0.94)";
      const countdownY = this.renderedPrice > 0
        ? Math.max(18, Math.min(plotHeight - 22, priceToY(this.renderedPrice) - 9))
        : 25;
      ctx.fillRect(badgeX, countdownY, badgeWidth, 18);
      ctx.fillStyle = "#f8fafc";
      ctx.textAlign = "center";
      ctx.fillText(countdown, badgeX + badgeWidth / 2, countdownY + 13);
      ctx.restore();
    }

    // 5A. Current Live Price Marker at Last Candle Tip
    if (this.renderedPrice > 0) {
      const currentY = priceToY(this.renderedPrice);
      const lastCandleX = xForCandleIndex(this.candles.length - 1);
      const lastCandle = this.candles[this.candles.length - 1];
      const isUp = this.renderedPrice >= (lastCandle ? lastCandle.open : this.renderedPrice);
      const tickColor = isUp ? "#10b981" : "#f43f5e";
      const tickRgb = isUp ? "16, 185, 129" : "244, 63, 94";

      // Parallel visible dotted reference guide across the chart to the left
      ctx.strokeStyle = `rgba(${tickRgb}, 0.65)`;
      ctx.lineWidth = 1.35;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, currentY);
      ctx.lineTo(lastCandleX, currentY);
      ctx.stroke();

      // Active vibrant connecting price line from last candle tip to right axis
      ctx.strokeStyle = tickColor;
      ctx.lineWidth = 1.6;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(lastCandleX, currentY);
      ctx.lineTo(plotWidth, currentY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Animated live pulsing price movement bead on the last candle tip
      const pulseTime = Date.now() / 1000;
      const pulseCycle = (pulseTime * 2.2) % 1;
      const pulseRadius = 5 + pulseCycle * 14;
      const pulseAlpha = (1 - pulseCycle) * 0.75;

      // Expanding radar ring
      ctx.fillStyle = `rgba(${tickRgb}, ${pulseAlpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(lastCandleX, currentY, pulseRadius, 0, Math.PI * 2);
      ctx.fill();

      // Inner glow halo
      ctx.fillStyle = `rgba(${tickRgb}, 0.38)`;
      ctx.beginPath();
      ctx.arc(lastCandleX, currentY, 6.5, 0, Math.PI * 2);
      ctx.fill();

      // Central solid bright core dot
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(lastCandleX, currentY, 3, 0, Math.PI * 2);
      ctx.fill();

      // Outer crisp stroke
      ctx.strokeStyle = tickColor;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(lastCandleX, currentY, 3.5, 0, Math.PI * 2);
      ctx.stroke();

      // Interactive Price Badge on Right Axis
      const badgeWidth = chartRightPadding - 4;
      const badgeHeight = 22;
      const badgeX = plotWidth + 1;
      const badgeY = currentY - badgeHeight / 2;

      // Pointer triangle pointing directly towards the candle tip
      ctx.fillStyle = tickColor;
      ctx.beginPath();
      ctx.moveTo(badgeX, currentY);
      ctx.lineTo(badgeX + 6, currentY - 5);
      ctx.lineTo(badgeX + 6, currentY + 5);
      ctx.closePath();
      ctx.fill();

      // Badge rectangle with rounded corners
      ctx.fillStyle = tickColor;
      ctx.beginPath();
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(badgeX + 5, badgeY, badgeWidth - 5, badgeHeight, 4);
      } else {
        ctx.rect(badgeX + 5, badgeY, badgeWidth - 5, badgeHeight);
      }
      ctx.fill();

      // Formatted live price text
      ctx.fillStyle = "#ffffff";
      ctx.font = 'bold 11px "JetBrains Mono", monospace';
      ctx.textAlign = "center";
      ctx.fillText(
        this.formatPrice(this.renderedPrice),
        badgeX + 5 + (badgeWidth - 5) / 2,
        currentY + 4,
      );
      ctx.textAlign = "left";
    }

    // 5B. Active Position Strike Price Overlay
    this.tradeOverlays.forEach((tradeOverlay, overlayIndex) => {
      if (!tradeOverlay || typeof tradeOverlay.strikePrice !== "number" || isNaN(tradeOverlay.strikePrice) || tradeOverlay.strikePrice <= 0) return;
      const strikeY = priceToY(tradeOverlay.strikePrice);
      if (typeof strikeY !== "number" || isNaN(strikeY)) return;
      const isCall = tradeOverlay.direction === "CALL";
      const color = isCall ? "#10b981" : "#f43f5e";
      const isITM = isCall
        ? this.latestPrice >= tradeOverlay.strikePrice
        : this.latestPrice <= tradeOverlay.strikePrice;

      // Strike Line
      const isPending = tradeOverlay.kind === "PENDING";
      ctx.strokeStyle = color;
      ctx.lineWidth = isPending ? 1.2 : 1.5;
      ctx.setLineDash(isPending ? [3, 4] : [6, 3]);
      ctx.beginPath();
      ctx.moveTo(0, strikeY);
      ctx.lineTo(plotWidth, strikeY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Strike Badge
      const stakeNum = parseFloat(tradeOverlay.stake) || 0;
      const payoutPct = parseFloat(tradeOverlay.payoutPercentage) || 0;
      const profitStr = ((stakeNum * payoutPct) / 100).toFixed(2);
      const badgeText = this.shortOrderLabel
        ? `${tradeOverlay.direction === "CALL" ? "▲" : "▼"} $${stakeNum.toFixed(2)}`
        : isPending
        ? `${tradeOverlay.label || "PENDING"} · ${tradeOverlay.direction === "CALL" ? "UP" : "DOWN"} @ ${this.formatPrice(tradeOverlay.strikePrice)}`
        : `${tradeOverlay.direction} @ ${this.formatPrice(tradeOverlay.strikePrice)} (${isITM ? "ITM +$" + profitStr : "OTM"})`;
      ctx.font = 'bold 11px "JetBrains Mono", monospace';
      const textWidth = ctx.measureText(badgeText).width;
      ctx.fillStyle = color;
      ctx.fillRect(10, strikeY - 18 - overlayIndex * 2, textWidth + 16, 20);
      ctx.fillStyle = "#020617";
      ctx.fillText(badgeText, 18, strikeY - 4 - overlayIndex * 2);
    });

    // 6. Crosshair Overlay
    if (
      this.crosshair &&
      this.crosshair.x <= plotWidth &&
      this.crosshair.y <= plotHeight
    ) {
      const { x, y } = this.crosshair;
      ctx.strokeStyle = "#94a3b8";
      ctx.setLineDash([2, 2]);

      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, plotHeight);
      ctx.moveTo(0, y);
      ctx.lineTo(plotWidth, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Crosshair price tag
      const crosshairPrice = maxPrice - (y / plotHeight) * priceRange;
      ctx.fillStyle = currentTheme === "light" ? "#0f172a" : "#334155";
      ctx.fillRect(plotWidth + 1, y - 9, chartRightPadding - 2, 18);
      ctx.fillStyle = "#ffffff";
      ctx.fillText(this.formatPrice(crosshairPrice), plotWidth + 6, y + 4);

      const nearestIndex = Math.max(0, Math.min(this.candles.length - 1,
        Math.round((x - liveAnchorX) / candleSpacing) + this.candles.length - 1));
      const candle = this.candles[nearestIndex];
      if (candle) {
        const timeParts = new Intl.DateTimeFormat("en-GB", {
          year: "numeric", month: "2-digit", day: "2-digit",
          hour: "2-digit", minute: "2-digit", second: "2-digit",
          hour12: false, timeZone: this.chartTimezone
        }).formatToParts(new Date(candle.time * 1000));
        const getP = (type: string) => timeParts.find((p) => p.type === type)?.value || "";
        const stamp = `${getP("year")}-${getP("month")}-${getP("day")} ${getP("hour")}:${getP("minute")}:${getP("second")} ${this.chartTimezoneLabel}`;
        const lines = [
          stamp,
          `O ${this.formatPrice(candle.open)}  H ${this.formatPrice(candle.high)}`,
          `L ${this.formatPrice(candle.low)}  C ${this.formatPrice(candle.close)}`,
        ];
        if (this.volumeVisible) {
          const displayVol = this.computeDisplayVolume(candle);
          lines.push(`Vol: ${displayVol.toLocaleString()}`);
        }
        ctx.font = '600 10px "JetBrains Mono", monospace';
        const tooltipWidth = Math.max(...lines.map((line) => ctx.measureText(line).width)) + 16;
        const tooltipHeight = 10 + lines.length * 15;
        const tooltipX = Math.min(plotWidth - tooltipWidth - 6, Math.max(6, x + 12));
        const tooltipY = Math.min(plotHeight - tooltipHeight - 6, Math.max(8, y + 12));
        ctx.fillStyle = currentTheme === "light" ? "rgba(255,255,255,.96)" : "rgba(15,23,42,.96)";
        ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);
        ctx.fillStyle = currentTheme === "light" ? "#0f172a" : "#e2e8f0";
        ctx.textAlign = "left";
        lines.forEach((line, index) => ctx.fillText(line, tooltipX + 8, tooltipY + 14 + index * 15));
      }
    }

    // 7. Degraded / Reconnecting Overlay
    if (this.isDegraded) {
      ctx.fillStyle = "rgba(15, 23, 42, 0.75)";
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = "#f59e0b";
      ctx.font = "bold 15px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(
        "⚠️ Market Feed Degraded — Trading Halted",
        width / 2,
        height / 2,
      );
    }

    if (this.settlementPulse && this.settlementPulse.until > nowMs) {
      const remaining = this.settlementPulse.until - nowMs;
      const alpha = Math.min(1, remaining / 450);
      const won = this.settlementPulse.status === "SETTLED_WIN";
      const tied = this.settlementPulse.status === "SETTLED_TIE";
      const label = won ? "TRADE WON" : tied ? "TRADE TIED" : "TRADE SETTLED";
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = won ? "rgba(16,185,129,.94)" : tied ? "rgba(59,130,246,.94)" : "rgba(244,63,94,.94)";
      const boxWidth = 150;
      ctx.fillRect(plotWidth / 2 - boxWidth / 2, plotHeight * 0.16, boxWidth, 34);
      ctx.fillStyle = "#fff";
      ctx.font = '800 12px "Inter", sans-serif';
      ctx.textAlign = "center";
      ctx.fillText(label, plotWidth / 2, plotHeight * 0.16 + 21);
      ctx.restore();
    } else if (this.settlementPulse) {
      this.settlementPulse = null;
    }
  }

  public destroy() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.inertiaFrameId !== null) cancelAnimationFrame(this.inertiaFrameId);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
